import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  Carousel, ProgressBar
} from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const FacilitiesManager = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [tempPhotos, setTempPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // API configuration
  const API_BASE_URL = BASE_URL;
//   const UPLOAD_BASE_URL = UPLOAD_BASE_URL;
  
  const [formData, setFormData] = useState({
    facility_name: '',
    description: '',
    status: 'Active',
    uploaded_by: 1,
    photos: []
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/facilities.php`);
      console.log('Facilities data:', response.data);
      
      if (response.data.success) {
        setFacilities(response.data.data);
      } else {
        setError('Failed to load facilities: ' + response.data.message);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    try {
      setUploadingPhotos(true);
      setUploadProgress(0);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Add all photos to form data
      files.forEach((file) => {
        formData.append('photos[]', file);
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/upload_facility_photos.php`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        const uploadedPhotos = response.data.uploaded_files.map(file => ({
          photo_path: file.photo_path,
          alt_text: '',
          thumbnail_path: file.thumbnail_path
        }));
        
        setTempPhotos(prev => [...prev, ...uploadedPhotos]);
        setSuccess(`${uploadedPhotos.length} photo(s) uploaded successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to upload photos: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading photos: ' + err.message);
    } finally {
      setUploadingPhotos(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveTempPhoto = (index) => {
    setTempPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingPhoto = (photoId) => {
    setPhotosToDelete(prev => [...prev, photoId]);
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId)
    }));
  };

  const handlePhotoDragEnd = (result) => {
    if (!result.destination) return;
    
    const photos = editingFacility 
      ? [...formData.photos] 
      : [...tempPhotos];
    
    const [reorderedItem] = photos.splice(result.source.index, 1);
    photos.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedPhotos = photos.map((photo, index) => ({
      ...photo,
      display_order: index + 1
    }));
    
    if (editingFacility) {
      setFormData(prev => ({
        ...prev,
        photos: updatedPhotos
      }));
    } else {
      setTempPhotos(updatedPhotos);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let dataToSend = { ...formData };
      
      if (editingFacility) {
        // For editing, handle photos updates
        dataToSend.photos_to_add = tempPhotos;
        dataToSend.photos_to_delete = photosToDelete;
        
        // Reordering info
        const photosOrder = formData.photos.map((photo, index) => ({
          id: photo.id,
          display_order: index + 1
        }));
        dataToSend.photos_order = photosOrder;
        
        delete dataToSend.photos; // Remove original photos array
        
        const response = await axios.put(
          `${API_BASE_URL}/facilities.php?id=${editingFacility.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Facility updated successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      } else {
        // For new facility, include temp photos
        dataToSend.photos = tempPhotos;
        
        const response = await axios.post(
          `${API_BASE_URL}/facilities.php`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Facility added successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      }
      
      setShowModal(false);
      resetForm();
      setEditingFacility(null);
      fetchFacilities();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError('Error saving facility: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this facility? All associated photos will also be deleted.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_BASE_URL}/facilities.php?id=${id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success) {
          setSuccess('Facility deleted successfully!');
          fetchFacilities();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete facility: ' + (response.data.message || ''));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Error deleting facility: ' + err.message);
      }
    }
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setFormData({
      facility_name: facility.facility_name,
      description: facility.description || '',
      status: facility.status,
      uploaded_by: facility.uploaded_by || 1,
      photos: facility.photos || []
    });
    setTempPhotos([]);
    setPhotosToDelete([]);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      facility_name: '',
      description: '',
      status: 'Active',
      uploaded_by: 1,
      photos: []
    });
    setTempPhotos([]);
    setPhotosToDelete([]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFacility(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading facilities...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Facilities Manager</h2>
            <p className="text-muted">Manage facilities with multiple photos support</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Facility
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="100">Photos</th>
                  <th>Facility Name</th>
                  <th>Description</th>
                  <th width="100">Status</th>
                  <th width="120">Photos Count</th>
                  <th width="120">Created</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>
              <tbody>
                {facilities.length > 0 ? (
                  facilities.map((facility) => (
                    <tr key={facility.id}>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {facility.photos && facility.photos.slice(0, 3).map((photo, idx) => (
                            <Image
                              key={photo.id || idx}
                              src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                              alt={photo.alt_text || facility.facility_name}
                              rounded
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              className="border"
                              title={photo.alt_text || `Photo ${idx + 1}`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://via.placeholder.com/40x40?text=${facility.facility_name.charAt(0)}`;
                              }}
                            />
                          ))}
                          {facility.photos && facility.photos.length > 3 && (
                            <Badge bg="secondary" className="align-self-center">
                              +{facility.photos.length - 3}
                            </Badge>
                          )}
                          {(!facility.photos || facility.photos.length === 0) && (
                            <div className="text-muted small">
                              No photos
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <strong>{facility.facility_name}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {facility.description 
                            ? (facility.description.length > 80 
                                ? `${facility.description.substring(0, 80)}...` 
                                : facility.description)
                            : 'No description'}
                        </small>
                      </td>
                      <td>
                        <Badge bg={facility.status === 'Active' ? 'success' : 'secondary'}>
                          {facility.status}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg="info">
                          {facility.photos ? facility.photos.length : 0} photos
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(facility.uploaded_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(facility)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(facility.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="fas fa-building fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No facilities found</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i> Add First Facility
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingFacility ? 'Edit Facility' : 'Add New Facility'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Facility Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="facility_name"
                      value={formData.facility_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Swimming Pool, Library"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe the facility in detail..."
                />
              </Form.Group>
              
              {/* Photo Management Section */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-images me-2"></i>
                    Photos Management
                  </h6>
                  <small className="text-muted">Upload multiple photos (Max 5MB each, Drag to reorder)</small>
                </Card.Header>
                <Card.Body>
                  {/* Upload Progress */}
                  {uploadingPhotos && (
                    <div className="mb-3">
                      <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
                      <small className="text-muted d-block mt-1">
                        Uploading photos...
                      </small>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <Form.Group className="mb-4">
                    <Form.Label>Upload New Photos</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhotos}
                      multiple
                    />
                    <Form.Text className="text-muted">
                      Select multiple photos (JPG, PNG, GIF, WebP). Max 5MB each.
                    </Form.Text>
                  </Form.Group>
                  
                  {/* Photos Grid with Drag & Drop */}
                  <h6>Photos ({formData.photos.length + tempPhotos.length})</h6>
                  <DragDropContext onDragEnd={handlePhotoDragEnd}>
                    <Droppable droppableId="photos" direction="horizontal">
                      {(provided) => (
                        <div 
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="d-flex flex-wrap gap-3 mb-3"
                        >
                          {/* Existing Photos */}
                          {formData.photos.map((photo, index) => (
                            <Draggable 
                              key={`existing-${photo.id}`} 
                              draggableId={`existing-${photo.id}`} 
                              index={index}
                              isDragDisabled={photosToDelete.includes(photo.id)}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`position-relative ${photosToDelete.includes(photo.id) ? 'opacity-50' : ''}`}
                                  style={{ width: '180px' }}
                                >
                                  <Card className="border">
                                    <Card.Img
                                      variant="top"
                                      src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                                      alt={photo.alt_text || `Photo ${index + 1}`}
                                      style={{ height: '120px', objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://via.placeholder.com/180x120?text=Photo+${index + 1}`;
                                      }}
                                    />
                                    <Card.Body className="p-2">
                                      <Form.Control
                                        type="text"
                                        placeholder="Alt text (description)"
                                        value={photo.alt_text || ''}
                                        onChange={(e) => {
                                          const newPhotos = [...formData.photos];
                                          newPhotos[index].alt_text = e.target.value;
                                          setFormData(prev => ({ ...prev, photos: newPhotos }));
                                        }}
                                        size="sm"
                                      />
                                    </Card.Body>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="position-absolute top-0 end-0 m-1"
                                      onClick={() => handleRemoveExistingPhoto(photo.id)}
                                      title="Remove photo"
                                    >
                                      <i className="fas fa-times"></i>
                                    </Button>
                                    <div className="position-absolute top-0 start-0 m-1">
                                      <Badge bg="info" style={{ cursor: 'move' }}>
                                        <i className="fas fa-grip-vertical me-1"></i>
                                        {index + 1}
                                      </Badge>
                                    </div>
                                  </Card>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          
                          {/* New Temporary Photos */}
                          {tempPhotos.map((photo, index) => (
                            <Draggable 
                              key={`temp-${index}`} 
                              draggableId={`temp-${index}`} 
                              index={formData.photos.length + index}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{ width: '180px' }}
                                >
                                  <Card className="border">
                                    <Card.Img
                                      variant="top"
                                      src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                                      alt={photo.alt_text || `New photo ${index + 1}`}
                                      style={{ height: '120px', objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://via.placeholder.com/180x120?text=New+${index + 1}`;
                                      }}
                                    />
                                    <Card.Body className="p-2">
                                      <Form.Control
                                        type="text"
                                        placeholder="Alt text (description)"
                                        value={photo.alt_text || ''}
                                        onChange={(e) => {
                                          const newPhotos = [...tempPhotos];
                                          newPhotos[index].alt_text = e.target.value;
                                          setTempPhotos(newPhotos);
                                        }}
                                        size="sm"
                                      />
                                    </Card.Body>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="position-absolute top-0 end-0 m-1"
                                      onClick={() => handleRemoveTempPhoto(index)}
                                      title="Remove photo"
                                    >
                                      <i className="fas fa-times"></i>
                                    </Button>
                                    <div className="position-absolute top-0 start-0 m-1">
                                      <Badge bg="warning" style={{ cursor: 'move' }}>
                                        <i className="fas fa-grip-vertical me-1"></i>
                                        {formData.photos.length + index + 1}
                                      </Badge>
                                    </div>
                                  </Card>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  
                  {/* No Photos Message */}
                  {(formData.photos.length === 0 && tempPhotos.length === 0) && (
                    <div className="text-center py-4 border rounded bg-light">
                      <i className="fas fa-images fa-2x text-muted mb-3"></i>
                      <p className="text-muted">No photos added yet</p>
                      <small className="d-block text-muted">
                        Upload photos to showcase this facility
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
              
              {/* Preview Section */}
              <Card className="border-secondary">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-eye me-2"></i>
                    Preview
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={5}>
                      {/* Photo Carousel Preview */}
                      <div style={{ maxHeight: '300px', overflow: 'hidden' }}>
                        {formData.photos.length + tempPhotos.length > 0 ? (
                          <Carousel>
                            {[...formData.photos, ...tempPhotos].map((photo, index) => (
                              <Carousel.Item key={index}>
                                <Image
                                  src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                                  alt={photo.alt_text || formData.facility_name}
                                  fluid
                                  style={{ height: '250px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://via.placeholder.com/400x250?text=Preview+${index + 1}`;
                                  }}
                                />
                                <Carousel.Caption className="bg-dark bg-opacity-50 py-1">
                                  <small>{photo.alt_text || `Photo ${index + 1}`}</small>
                                </Carousel.Caption>
                              </Carousel.Item>
                            ))}
                          </Carousel>
                        ) : (
                          <div className="text-center py-5 border rounded bg-light">
                            <i className="fas fa-image fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No photos to preview</p>
                          </div>
                        )}
                      </div>
                    </Col>
                    
                    <Col md={7}>
                      <h4>{formData.facility_name || 'Facility Name'}</h4>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <Badge bg={formData.status === 'Active' ? 'success' : 'secondary'}>
                          {formData.status}
                        </Badge>
                        <Badge bg="info">
                          {formData.photos.length + tempPhotos.length} photos
                        </Badge>
                      </div>
                      
                      {formData.description && (
                        <div className="mb-3">
                          <p className="text-muted mb-0">{formData.description}</p>
                        </div>
                      )}
                      
                      <div className="small text-muted mt-4">
                        <div className="d-flex flex-wrap gap-4">
                          <div>
                            <i className="fas fa-images me-1"></i>
                            Total Photos: <strong>{formData.photos.length + tempPhotos.length}</strong>
                          </div>
                          {photosToDelete.length > 0 && (
                            <div className="text-danger">
                              <i className="fas fa-trash me-1"></i>
                              Marked for deletion: <strong>{photosToDelete.length}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingPhotos}>
                {uploadingPhotos ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  editingFacility ? 'Update Facility' : 'Add Facility'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default FacilitiesManager;